'use client';

import { useCallback, useRef, useState } from 'react';

interface ImageQueueItem {
  src: string;
  onLoad: () => void;
  onError: () => void;
}

const MAX_CONCURRENT_IMAGES = 6; // Ограничиваем количество одновременных загрузок
const activeLoads = new Set<string>();
const pendingQueue: ImageQueueItem[] = [];

export const useImageQueue = () => {
  const [loadingStates, setLoadingStates] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  const imageRefs = useRef<Record<string, HTMLImageElement>>({});

  const processQueue = useCallback(() => {
    while (pendingQueue.length > 0 && activeLoads.size < MAX_CONCURRENT_IMAGES) {
      const item = pendingQueue.shift();
      if (!item) break;

      activeLoads.add(item.src);
      
      const img = new Image();
      imageRefs.current[item.src] = img;
      
      img.onload = () => {
        activeLoads.delete(item.src);
        item.onLoad();
        processQueue(); // Обрабатываем следующий элемент в очереди
      };
      
      img.onerror = () => {
        activeLoads.delete(item.src);
        item.onError();
        processQueue(); // Обрабатываем следующий элемент в очереди
      };
      
      img.src = item.src;
    }
  }, []);

  const loadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Если изображение уже загружено или загружается
      if (loadingStates[src] === 'loaded') {
        resolve();
        return;
      }
      
      if (loadingStates[src] === 'error') {
        reject(new Error('Image failed to load'));
        return;
      }

      // Устанавливаем состояние загрузки
      setLoadingStates(prev => ({ ...prev, [src]: 'loading' }));

      const handleLoad = () => {
        setLoadingStates(prev => ({ ...prev, [src]: 'loaded' }));
        resolve();
      };

      const handleError = () => {
        setLoadingStates(prev => ({ ...prev, [src]: 'error' }));
        reject(new Error('Image failed to load'));
      };

      // Добавляем в очередь
      pendingQueue.push({ src, onLoad: handleLoad, onError: handleError });
      processQueue();
    });
  }, [loadingStates, processQueue]);

  const getImageState = useCallback((src: string) => {
    return loadingStates[src] || 'loading';
  }, [loadingStates]);

  const preloadImage = useCallback((src: string) => {
    if (!loadingStates[src]) {
      loadImage(src).catch(() => {
        // Игнорируем ошибки при preload
      });
    }
  }, [loadImage, loadingStates]);

  return {
    loadImage,
    getImageState,
    preloadImage,
  };
}; 